import { EXTERNAL_COLUMN_DEF_KEYS } from './types';

export type { ExternalColumnDef, ExternalTableSchema } from './types';

export interface ValidationResult {
  valid: boolean;
  errors: ValidationIssue[];
  warnings: ValidationIssue[];
}

export interface ValidationIssue {
  path: string;
  message: string;
  severity: 'critical' | 'error' | 'warning';
  code: string;
}

export const VALID_COLUMN_TYPES = new Set([
  'text',
  'html',
  'date',
  'dateTime',
  'dateTimeLTS',
  'dateRange',
  'conditionalDate',
  'number',
  'currency',
  'percentage',
  'badge',
  'couple',
  'coupleWithDateRange',
  'link',
  'modal',
  'mask',
  'conditional',
  'space',
  'time',
  'arraySplit',
  'custom',
]);

export const VALID_EDITOR_TYPES = new Set([
  'text',
  'number',
  'date',
  'dateTime',
  'select',
  'asyncSelect',
  'dynamicSelect',
  'checkbox',
  'textarea',
  'checkboxDriven',
  'string',
]);

// Canonical values + backwards-compat aliases (normalizer maps the aliases away)
const VALID_VISIBILITY = new Set([
  'locked', 'picker', 'hidden',          // canonical
  'mandatory', 'default', 'optional', 'internal', // deprecated aliases
]);
const VALID_ALIGN = new Set(['left', 'center', 'right']);

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return v !== null && typeof v === 'object' && !Array.isArray(v);
}

function isNonEmptyString(v: unknown): v is string {
  return typeof v === 'string' && v.trim().length > 0;
}

export class SchemaValidationError extends Error {
  constructor(public issues: ValidationIssue[]) {
    super(`Schema validation failed: ${issues.map((i) => i.message).join('; ')}`);
    this.name = 'SchemaValidationError';
  }
}

function push(
  bucket: ValidationIssue[],
  path: string,
  message: string,
  severity: ValidationIssue['severity'],
  code: string,
): void {
  bucket.push({ path, message, severity, code });
}

function validateDataSourceObject(
  ds: unknown,
  path: string,
  errors: ValidationIssue[],
  warnings: ValidationIssue[],
): void {
  if (ds === undefined || ds === null) {
    return;
  }
  if (!isPlainObject(ds)) {
    push(errors, path, 'dataSource must be an object', 'error', 'DATA_SOURCE_NOT_OBJECT');
    return;
  }
  const url = ds.url;
  const endpoint = ds.endpoint;
  const urlOk = isNonEmptyString(url);
  const endpointOk = isNonEmptyString(endpoint);
  if (!urlOk && !endpointOk) {
    push(
      errors,
      path,
      'dataSource must include a non-empty url (or endpoint for table-level fetch config)',
      'error',
      'DATA_SOURCE_URL_REQUIRED',
    );
  }

  const dsRec = ds as Record<string, unknown>;
  const hasContinuation = dsRec.continuationCursor != null;
  const hasLegacyLoadMore = dsRec.loadMoreCursor != null;
  const lmc = hasContinuation ? dsRec.continuationCursor : dsRec.loadMoreCursor;
  const cursorPathKey = hasContinuation ? 'continuationCursor' : 'loadMoreCursor';

  if (hasContinuation && hasLegacyLoadMore) {
    push(
      warnings,
      `${path}.loadMoreCursor`,
      'loadMoreCursor is ignored when continuationCursor is also set; remove loadMoreCursor.',
      'warning',
      'CONTINUATION_CURSOR_DUPLICATE_KEY',
    );
  } else if (!hasContinuation && hasLegacyLoadMore) {
    push(
      warnings,
      `${path}.loadMoreCursor`,
      'Renamed to dataSource.continuationCursor (same shape). Prefer continuationCursor.',
      'warning',
      'CONTINUATION_CURSOR_LEGACY_KEY',
    );
  }

  if (lmc !== undefined && lmc !== null) {
    if (!isPlainObject(lmc)) {
      push(
        errors,
        `${path}.${cursorPathKey}`,
        'continuationCursor must be an object',
        'error',
        'CONTINUATION_CURSOR_NOT_OBJECT',
      );
    } else {
      if (!isNonEmptyString(lmc.queryKey)) {
        push(
          errors,
          `${path}.${cursorPathKey}.queryKey`,
          'continuationCursor.queryKey must be a non-empty string',
          'error',
          'CONTINUATION_CURSOR_QUERY_KEY',
        );
      }
      if (!isNonEmptyString(lmc.fieldPath)) {
        push(
          errors,
          `${path}.${cursorPathKey}.fieldPath`,
          'continuationCursor.fieldPath must be a non-empty string',
          'error',
          'CONTINUATION_CURSOR_FIELD_PATH',
        );
      }
      const fmt = lmc.format;
      if (
        fmt !== undefined &&
        fmt !== null &&
        fmt !== 'preserve' &&
        fmt !== 'iso' &&
        fmt !== 'mmddyyyy'
      ) {
        push(
          warnings,
          `${path}.${cursorPathKey}.format`,
          `continuationCursor.format "${String(fmt)}" is unknown — use preserve, iso, or mmddyyyy`,
          'warning',
          'CONTINUATION_CURSOR_FORMAT_UNKNOWN',
        );
      }
    }
  }
}

function validateUnknownColumnKeys(
  col: Record<string, unknown>,
  path: string,
  warnings: ValidationIssue[],
): void {
  for (const key of Object.keys(col)) {
    if (!EXTERNAL_COLUMN_DEF_KEYS.has(key)) {
      push(
        warnings,
        `${path}.${key}`,
        `Unknown column property "${key}" — not part of ExternalColumnDef`,
        'warning',
        'UNKNOWN_COLUMN_PROPERTY',
      );
    }
  }
}

function validateColumn(
  col: unknown,
  index: number,
  pathPrefix: string,
  seenFields: Set<string>,
  errors: ValidationIssue[],
  warnings: ValidationIssue[],
): void {
  const path = `${pathPrefix}[${index}]`;

  if (!isPlainObject(col)) {
    push(errors, path, 'Column must be an object', 'critical', 'COLUMN_NOT_OBJECT');
    return;
  }

  validateUnknownColumnKeys(col, path, warnings);

  const field = col.field;
  if (!isNonEmptyString(field)) {
    push(
      errors,
      `${path}.field`,
      'Column must have a non-empty string field',
      'critical',
      'COLUMN_FIELD_REQUIRED',
    );
  } else if (seenFields.has(field)) {
    push(
      errors,
      `${path}.field`,
      `Duplicate column field "${field}" at this level`,
      'critical',
      'COLUMN_FIELD_DUPLICATE',
    );
  } else {
    seenFields.add(field);
  }

  const header = col.header;
  const colTypeRaw = col.type;
  const isSpacerColumn = colTypeRaw === 'space';
  if (!isNonEmptyString(header) && !isSpacerColumn) {
    push(
      warnings,
      `${path}.header`,
      'Column should have a non-empty string header (will be auto-generated from field name)',
      'warning',
      'COLUMN_HEADER_MISSING',
    );
  }

  const colType = col.type;
  if (colType !== undefined && colType !== null) {
    if (typeof colType !== 'string' || !VALID_COLUMN_TYPES.has(colType)) {
      push(
        errors,
        `${path}.type`,
        `Invalid column type "${String(colType)}"`,
        'critical',
        'COLUMN_TYPE_INVALID',
      );
    }
  }

  const visibility = col.visibility;
  if (visibility !== undefined && visibility !== null) {
    if (typeof visibility !== 'string' || !VALID_VISIBILITY.has(visibility)) {
      push(
        warnings,
        `${path}.visibility`,
        `visibility should be one of: "locked", "picker", "hidden" — or omit for the default (got "${String(visibility)}")`,
        'warning',
        'VISIBILITY_INVALID',
      );
    } else if (['mandatory', 'default', 'optional', 'internal'].includes(visibility)) {
      const map: Record<string, string> = {
        mandatory: '"locked"',
        default: 'omit (it is the default)',
        optional: '"picker"',
        internal: '"hidden"',
      };
      push(
        warnings,
        `${path}.visibility`,
        `"${visibility}" is a deprecated alias — use ${map[visibility]} instead`,
        'warning',
        'VISIBILITY_DEPRECATED',
      );
    }
  }

  const align = col.align;
  if (align !== undefined && align !== null) {
    if (typeof align !== 'string' || !VALID_ALIGN.has(align)) {
      push(
        warnings,
        `${path}.align`,
        `align should be one of: left, center, right (got "${String(align)}")`,
        'warning',
        'ALIGN_INVALID',
      );
    }
  }

  const typeStr = typeof colType === 'string' ? colType : undefined;

  if (typeStr === 'badge') {
    const badge = col.badge;
    if (!isPlainObject(badge) || !('statusMap' in badge) || badge.statusMap == null) {
      push(
        errors,
        `${path}.badge`,
        'Columns with type "badge" require badge.statusMap',
        'error',
        'BADGE_STATUS_MAP_REQUIRED',
      );
    } else if (typeof badge.statusMap !== 'object' || Array.isArray(badge.statusMap)) {
      push(
        errors,
        `${path}.badge.statusMap`,
        'badge.statusMap must be an object',
        'error',
        'BADGE_STATUS_MAP_INVALID',
      );
    }
  }

  if (typeStr === 'couple') {
    const couple = col.couple;
    if (!isPlainObject(couple) || !isNonEmptyString(couple.subtitleField)) {
      push(
        errors,
        `${path}.couple`,
        'Columns with type "couple" require couple.subtitleField (non-empty string)',
        'error',
        'COUPLE_SUBTITLE_REQUIRED',
      );
    }
  }

  if (typeStr === 'dateRange') {
    const dr = col.dateRange;
    if (
      !isPlainObject(dr) ||
      !isNonEmptyString(dr.startField) ||
      !isNonEmptyString(dr.endField)
    ) {
      push(
        errors,
        `${path}.dateRange`,
        'Columns with type "dateRange" require dateRange.startField and dateRange.endField (non-empty strings)',
        'error',
        'DATE_RANGE_FIELDS_REQUIRED',
      );
    }
  }

  if (typeStr === 'mask') {
    const mask = col.mask;
    if (!isPlainObject(mask) || !isNonEmptyString(mask.valueField)) {
      push(
        errors,
        `${path}.mask`,
        'Columns with type "mask" require mask.valueField (non-empty string)',
        'error',
        'MASK_VALUE_FIELD_REQUIRED',
      );
    }
  }

  const editable = col.editable;
  if (editable !== undefined && editable !== null) {
    if (isPlainObject(editable) && 'type' in editable) {
      const et = editable.type;
      if (typeof et !== 'string' || !VALID_EDITOR_TYPES.has(et)) {
        push(
          errors,
          `${path}.editable.type`,
          `Invalid editable.type "${String(et)}"`,
          'error',
          'EDITABLE_TYPE_INVALID',
        );
      }
    }
  }

  if ('dataSource' in col && col.dataSource !== undefined && col.dataSource !== null) {
    validateDataSourceObject(col.dataSource, `${path}.dataSource`, errors, warnings);
  }
}

function validateColumnsArray(
  columns: unknown,
  pathPrefix: string,
  errors: ValidationIssue[],
  warnings: ValidationIssue[],
): Set<string> {
  const fields = new Set<string>();
  if (!Array.isArray(columns)) {
    return fields;
  }
  const seen = new Set<string>();
  for (let i = 0; i < columns.length; i++) {
    validateColumn(columns[i], i, pathPrefix, seen, errors, warnings);
    const col = columns[i];
    if (isPlainObject(col) && isNonEmptyString(col.field)) {
      fields.add(col.field);
    }
  }
  return fields;
}

function getSubRowColumnArrays(schema: Record<string, unknown>): string[] {
  const paths: string[] = [];
  const features = schema.features;
  if (isPlainObject(features)) {
    const rows = features.rows;
    if (isPlainObject(rows)) {
      const sub = rows.subRows;
      if (isPlainObject(sub) && 'columns' in sub) {
        paths.push('features.rows.subRows.columns');
      }
    }
  }
  const options = schema.options;
  if (isPlainObject(options)) {
    const row = options.row;
    if (isPlainObject(row)) {
      const sub = row.subRows;
      if (isPlainObject(sub) && 'columns' in sub) {
        paths.push('options.row.subRows.columns');
      }
    }
  }
  return paths;
}

function walkFilterDataSources(
  filters: unknown,
  basePath: string,
  errors: ValidationIssue[],
  warnings: ValidationIssue[],
): void {
  if (!Array.isArray(filters)) {
    return;
  }
  for (let i = 0; i < filters.length; i++) {
    const f = filters[i];
    if (!isPlainObject(f)) {
      continue;
    }
    if (f.dataSource !== undefined && f.dataSource !== null) {
      validateDataSourceObject(f.dataSource, `${basePath}[${i}].dataSource`, errors, warnings);
    }
  }
}

function validateComputeRules(
  rules: unknown,
  columnFields: Set<string>,
  errors: ValidationIssue[],
  warnings: ValidationIssue[],
): void {
  if (rules === undefined || rules === null) {
    return;
  }
  if (!Array.isArray(rules)) {
    push(errors, 'computeRules', 'computeRules must be an array', 'error', 'COMPUTE_RULES_NOT_ARRAY');
    return;
  }
  if (rules.length === 0) {
    push(
      warnings,
      'computeRules',
      'computeRules is an empty array — remove it instead of leaving []',
      'warning',
      'COMPUTE_RULES_EMPTY',
    );
    return;
  }
  for (let i = 0; i < rules.length; i++) {
    const rule = rules[i];
    const p = `computeRules[${i}]`;
    if (!isPlainObject(rule)) {
      push(errors, p, 'Each compute rule must be an object', 'error', 'COMPUTE_RULE_INVALID');
      continue;
    }
    const sources = rule.sourceFields;
    if (!Array.isArray(sources)) {
      push(
        errors,
        `${p}.sourceFields`,
        'computeRules[].sourceFields must be an array',
        'error',
        'COMPUTE_SOURCE_FIELDS_INVALID',
      );
    } else {
      for (let j = 0; j < sources.length; j++) {
        const sf = sources[j];
        if (typeof sf !== 'string' || !columnFields.has(sf)) {
          push(
            errors,
            `${p}.sourceFields[${j}]`,
            `sourceFields must reference existing column fields (unknown: "${String(sf)}")`,
            'error',
            'COMPUTE_SOURCE_FIELD_UNKNOWN',
          );
        }
      }
    }
    const target = rule.targetField;
    if (typeof target !== 'string' || !columnFields.has(target)) {
      push(
        errors,
        `${p}.targetField`,
        `targetField must reference an existing column field (unknown: "${String(target)}")`,
        'error',
        'COMPUTE_TARGET_FIELD_UNKNOWN',
      );
    }
  }
}

function validateOneAction(
  a: Record<string, unknown>,
  p: string,
  errors: ValidationIssue[],
  warnings: ValidationIssue[],
): void {
  if (!isNonEmptyString(a.label)) {
    push(errors, `${p}.label`, 'Action must have a non-empty label', 'error', 'ACTION_LABEL_REQUIRED');
  }
  if (a.actionType != null && !isNonEmptyString(a.actionType)) {
    push(
      errors,
      `${p}.actionType`,
      'Action must have a non-empty actionType when provided',
      'error',
      'ACTION_TYPE_REQUIRED',
    );
  }

  const ar = a;
  const hasDialog = ar.dialog != null;
  const hasPreflight = ar.preflightDialog != null;
  const hasLegacyConfirm = ar.confirm != null;
  const legacyCount = [hasDialog, hasPreflight, hasLegacyConfirm].filter(Boolean).length;
  if (legacyCount > 1) {
    push(
      warnings,
      `${p}.dialog`,
      'Use only one of dialog, preflightDialog, or confirm; dialog takes precedence when normalizing.',
      'warning',
      'ACTION_DIALOG_DUPLICATE_KEYS',
    );
  } else if (!hasDialog && hasPreflight) {
    push(
      warnings,
      `${p}.preflightDialog`,
      'Renamed to dialog (same shape). Prefer dialog in saved JSON.',
      'warning',
      'ACTION_DIALOG_LEGACY_PREFLIGHT',
    );
  } else if (!hasDialog && !hasPreflight && hasLegacyConfirm) {
    push(
      warnings,
      `${p}.confirm`,
      'Renamed to dialog (same shape + optional kind). Prefer dialog in saved JSON.',
      'warning',
      'ACTION_DIALOG_LEGACY_CONFIRM',
    );
  }

  const dlg = (hasDialog ? ar.dialog : hasPreflight ? ar.preflightDialog : ar.confirm) as Record<string, unknown> | null | undefined;
  if (dlg != null && dlg !== undefined) {
    const dp = hasDialog ? `${p}.dialog` : hasPreflight ? `${p}.preflightDialog` : `${p}.confirm`;
    if (!isPlainObject(dlg)) {
      push(errors, dp, 'dialog must be an object', 'error', 'ACTION_DIALOG_NOT_OBJECT');
    } else {
      if (!isNonEmptyString(dlg.title)) {
        push(errors, `${dp}.title`, 'dialog.title must be a non-empty string', 'error', 'ACTION_DIALOG_TITLE');
      }
      if (!isNonEmptyString(dlg.message)) {
        push(errors, `${dp}.message`, 'dialog.message must be a non-empty string', 'error', 'ACTION_DIALOG_MESSAGE');
      }
      const k = dlg.kind;
      if (k != null && k !== 'confirm' && k !== 'alert') {
        push(
          warnings,
          `${dp}.kind`,
          `dialog.kind "${String(k)}" is unknown — use confirm or alert`,
          'warning',
          'ACTION_DIALOG_KIND_UNKNOWN',
        );
      }
    }
  }

  // Validate onSuccess=backgroundJob requires backgroundJobConfig (informational warning)
  const onSuccess = a.onSuccess;
  if (onSuccess === 'backgroundJob') {
    if (a.backgroundJobConfig == null) {
      push(
        warnings,
        `${p}.backgroundJobConfig`,
        'onSuccess "backgroundJob" works best with backgroundJobConfig (title, message, confirmLabel, navigatePath). Built-in defaults are used when omitted.',
        'warning',
        'ACTION_BG_JOB_NO_CONFIG',
      );
    } else if (!isPlainObject(a.backgroundJobConfig)) {
      push(errors, `${p}.backgroundJobConfig`, 'backgroundJobConfig must be an object', 'error', 'ACTION_BG_JOB_CONFIG_NOT_OBJECT');
    }
  }

  const sal = a.selectAll as Record<string, unknown> | null | undefined;
  if (sal != null && sal !== undefined) {
    const sp = `${p}.selectAll`;
    if (!isPlainObject(sal)) {
      push(errors, sp, 'selectAll must be an object', 'error', 'ACTION_SELECT_ALL_NOT_OBJECT');
    } else {
      const validOnSuccess = new Set([
        'refetch',
        'toast',
        'toastAndRefetch',
        'removeRow',
        'openTab',
        'backgroundJob',
        'none',
      ]);
      const son = sal.onSuccess;
      if (son != null && typeof son === 'string' && !validOnSuccess.has(son)) {
        push(
          errors,
          `${sp}.onSuccess`,
          `selectAll.onSuccess must be one of: ${Array.from(validOnSuccess).join(', ')}`,
          'error',
          'ACTION_SELECT_ALL_ON_SUCCESS_INVALID',
        );
      }
      if (son === 'openTab') {
        const actionUrl = a.responseUrl;
        const selUrl = sal.responseUrl;
        const hasUrl =
          (typeof actionUrl === 'string' && actionUrl.trim().length > 0) ||
          (typeof selUrl === 'string' && selUrl.trim().length > 0);
        if (!hasUrl) {
          push(
            errors,
            `${sp}.onSuccess`,
            'selectAll.onSuccess "openTab" requires responseUrl on the action or selectAll.responseUrl',
            'error',
            'ACTION_SELECT_ALL_OPEN_TAB_NO_URL',
          );
        }
      }
      if (son === 'backgroundJob') {
        const hasCfg = a.backgroundJobConfig != null || sal.backgroundJobConfig != null;
        if (!hasCfg) {
          push(
            warnings,
            `${sp}.backgroundJobConfig`,
            'selectAll.onSuccess "backgroundJob" works best with backgroundJobConfig on the action and/or under selectAll (merged at runtime). Built-in defaults are used when both are omitted.',
            'warning',
            'ACTION_SELECT_ALL_BG_JOB_NO_CONFIG',
          );
        } else if (sal.backgroundJobConfig != null && !isPlainObject(sal.backgroundJobConfig)) {
          push(errors, `${sp}.backgroundJobConfig`, 'selectAll.backgroundJobConfig must be an object', 'error', 'ACTION_SELECT_ALL_BG_JOB_CONFIG_NOT_OBJECT');
        }
      }
      if (sal.responseUrl != null && typeof sal.responseUrl !== 'string') {
        push(errors, `${sp}.responseUrl`, 'selectAll.responseUrl must be a string', 'error', 'ACTION_SELECT_ALL_RESPONSE_URL_TYPE');
      }
      if (sal.successMessage != null && typeof sal.successMessage !== 'string') {
        push(errors, `${sp}.successMessage`, 'selectAll.successMessage must be a string', 'error', 'ACTION_SELECT_ALL_SUCCESS_MSG_TYPE');
      }
    }
  }
}

function validateActionArray(
  actions: unknown,
  pathPrefix: string,
  errors: ValidationIssue[],
  warnings: ValidationIssue[],
): void {
  if (actions === undefined || actions === null) {
    return;
  }
  if (!Array.isArray(actions)) {
    push(errors, pathPrefix, `${pathPrefix} must be an array`, 'error', 'ACTIONS_LIST_NOT_ARRAY');
    return;
  }
  for (let i = 0; i < actions.length; i++) {
    const a = actions[i];
    const p = `${pathPrefix}[${i}]`;
    if (!isPlainObject(a)) {
      push(errors, p, 'Each action must be an object', 'error', 'ACTION_INVALID');
      continue;
    }
    validateOneAction(a as Record<string, unknown>, p, errors, warnings);
  }
}

function validatePaginationFeatures(features: unknown, warnings: ValidationIssue[]): void {
  if (!isPlainObject(features)) {
    return;
  }
  const pagination = features.pagination;
  if (!isPlainObject(pagination)) {
    return;
  }
  const defaultSize = pagination.defaultSize;
  const options = pagination.options;
  if (
    typeof defaultSize === 'number' &&
    Array.isArray(options) &&
    options.length > 0 &&
    !options.includes(defaultSize)
  ) {
    push(
      warnings,
      'features.pagination.defaultSize',
      `defaultSize (${defaultSize}) is not listed in features.pagination.options`,
      'warning',
      'PAGINATION_DEFAULT_NOT_IN_OPTIONS',
    );
  }
}

/**
 * Validates an external DynamicTable V2 schema object before normalization.
 */
export function validateExternalSchema(schema: unknown): ValidationResult {
  const errors: ValidationIssue[] = [];
  const warnings: ValidationIssue[] = [];

  if (!isPlainObject(schema)) {
    push(errors, '', 'Schema must be a non-null object', 'critical', 'SCHEMA_NOT_OBJECT');
    push(errors, 'columns', 'columns must be a non-empty array', 'critical', 'COLUMNS_REQUIRED');
    return { valid: false, errors, warnings };
  }

  const columns = schema.columns;
  if (!Array.isArray(columns)) {
    push(errors, 'columns', 'columns must be a non-empty array', 'critical', 'COLUMNS_NOT_ARRAY');
  } else if (columns.length === 0) {
    push(errors, 'columns', 'columns must be a non-empty array', 'critical', 'COLUMNS_EMPTY');
  } else {
    validateColumnsArray(columns, 'columns', errors, warnings);
  }

  const topLevelFields = new Set<string>();
  if (Array.isArray(columns)) {
    for (const col of columns) {
      if (isPlainObject(col) && isNonEmptyString(col.field)) {
        topLevelFields.add(col.field);
      }
    }
  }

  for (const subPath of getSubRowColumnArrays(schema)) {
    const parts = subPath.split('.');
    let cur: unknown = schema;
    for (const part of parts) {
      if (!isPlainObject(cur)) {
        cur = undefined;
        break;
      }
      cur = cur[part];
    }
    if (cur !== undefined) {
      validateColumnsArray(cur, subPath, errors, warnings);
    }
  }

  if ('dataSource' in schema && schema.dataSource !== undefined && schema.dataSource !== null) {
    validateDataSourceObject(schema.dataSource, 'dataSource', errors, warnings);
    const ds = schema.dataSource;
    if (isPlainObject(ds) && ds.filters !== undefined) {
      walkFilterDataSources(ds.filters, 'dataSource.filters', errors, warnings);
    }
    if (isPlainObject(ds)) {
      const dsTop = ds as Record<string, unknown>;
      const cursorPresent =
        dsTop.continuationCursor != null || dsTop.loadMoreCursor != null;
      if (cursorPresent) {
        const feat = schema.features;
        const mode =
          isPlainObject(feat) && isPlainObject(feat.pagination)
            ? (feat.pagination as { mode?: string }).mode
            : undefined;
        if (mode !== 'loadMore' && mode !== 'infiniteScroll') {
          push(
            warnings,
            'dataSource.continuationCursor',
            'continuationCursor only applies when features.pagination.mode is "loadMore" or "infiniteScroll".',
            'warning',
            'CONTINUATION_CURSOR_IGNORED',
          );
        }
      }
    }
  }

  validateComputeRules(schema.computeRules, topLevelFields, errors, warnings);

  const actions = schema.actions;
  if (isPlainObject(actions)) {
    if (actions.row !== undefined) {
      validateActionArray(actions.row, 'actions.row', errors, warnings);
    }
    if (actions.bulk !== undefined) {
      validateActionArray(actions.bulk, 'actions.bulk', errors, warnings);
    }
    const toolbar = actions.toolbar;
    if (isPlainObject(toolbar)) {
      const hasNewApi = toolbar.buttons !== undefined || toolbar.templates !== undefined;
      const hasLegacyApi = toolbar.primary !== undefined || toolbar.secondary !== undefined;

      if (hasNewApi && hasLegacyApi) {
        push(
          warnings,
          'actions.toolbar',
          'Toolbar uses both new (buttons/templates) and legacy (primary/secondary) APIs. The new API takes precedence; legacy keys are ignored.',
          'warning',
          'TOOLBAR_MIXED_API',
        );
      }

      if (hasNewApi) {
        if (toolbar.buttons !== undefined) {
          validateActionArray(toolbar.buttons, 'actions.toolbar.buttons', errors, warnings);
          if (Array.isArray(toolbar.buttons)) {
            const VALID_TOOLBAR_VARIANTS = new Set([
              'primary',
              'secondary',
              'secondary-white',
              'primary-outline',
              'danger',
              'ghost',
            ]);
            for (let i = 0; i < toolbar.buttons.length; i++) {
              const btn = toolbar.buttons[i];
              if (isPlainObject(btn) && btn.variant != null) {
                if (typeof btn.variant !== 'string' || !VALID_TOOLBAR_VARIANTS.has(btn.variant)) {
                  push(
                    errors,
                    `actions.toolbar.buttons[${i}].variant`,
                    `variant must be one of: primary, secondary, secondary-white, primary-outline, danger, ghost (got "${String(btn.variant)}")`,
                    'error',
                    'TOOLBAR_BUTTON_VARIANT_INVALID',
                  );
                }
              }
            }
          }
        }
        if (toolbar.templates !== undefined) {
          if (!Array.isArray(toolbar.templates)) {
            push(errors, 'actions.toolbar.templates', 'actions.toolbar.templates must be an array', 'error', 'TOOLBAR_TEMPLATES_NOT_ARRAY');
          } else {
            const VALID_TEMPLATE_TYPES = new Set(['download', 'upload', 'menu']);
            for (let i = 0; i < toolbar.templates.length; i++) {
              const tmpl = toolbar.templates[i];
              const tp = `actions.toolbar.templates[${i}]`;
              if (!isPlainObject(tmpl)) {
                push(errors, tp, 'Each template must be an object', 'error', 'TOOLBAR_TEMPLATE_INVALID');
                continue;
              }
              if (!isNonEmptyString(tmpl.label)) {
                push(errors, `${tp}.label`, 'Template must have a non-empty label', 'error', 'TOOLBAR_TEMPLATE_LABEL_REQUIRED');
              }
              if (!isNonEmptyString(tmpl.type) || !VALID_TEMPLATE_TYPES.has(tmpl.type as string)) {
                push(errors, `${tp}.type`, `Template type must be one of: download, upload, menu (got "${String(tmpl.type)}")`, 'error', 'TOOLBAR_TEMPLATE_TYPE_INVALID');
              }
              if (!Array.isArray(tmpl.children) || tmpl.children.length === 0) {
                push(errors, `${tp}.children`, 'Template must have a non-empty children array', 'error', 'TOOLBAR_TEMPLATE_CHILDREN_REQUIRED');
              } else {
                validateActionArray(tmpl.children, `${tp}.children`, errors, warnings);
              }
            }
          }
        }
      } else {
        if (toolbar.primary !== undefined && toolbar.primary !== null) {
          const tp = toolbar.primary;
          const p = 'actions.toolbar.primary';
          if (!isPlainObject(tp)) {
            push(errors, p, 'actions.toolbar.primary must be an object', 'error', 'TOOLBAR_PRIMARY_INVALID');
          } else {
            validateOneAction(tp as Record<string, unknown>, p, errors, warnings);
          }
        }
        if (toolbar.secondary !== undefined) {
          validateActionArray(toolbar.secondary, 'actions.toolbar.secondary', errors, warnings);
        }
      }
    }
  }

  validatePaginationFeatures(schema.features, warnings);

  const valid = errors.length === 0;
  return { valid, errors, warnings };
}
